/**
 * game-guoji-xiangqi-fupan.js —— 学习乐园 · 国际象棋棋谱复盘（导入 PGN + 本地逐步分析 + 报告与回看）
 * ---------------------------------------------------------------------------
 * 分层与对战页完全一致：
 *   vendor/chesslib.min.js  规则层：PGN 解析、着法重放（本文件动态注入，见 ensureRules）
 *   chess-engine.js         计算层：bestMove / annotate / classify（**引擎零改动**）
 *   本文件                  界面层：导入、分步分析、报告渲染、棋盘回看
 *
 * 分析口径（不改引擎的前提下把「分差」算对）：
 *   对每个局面 bestMove(game, 2, ANALYZE_TIME) 取「轮走方最强应着」的白方视角分，存 scores[i]；
 *   第 i 步亏掉多少 = (scores[i] − scores[i+1]) × 行棋方向。相邻局面共用同一次搜索
 *   （局面 i+1 的搜索就是第 i+1 步之前那次），整局只要 N+1 次搜索。
 *   将杀 / 逼和的局面没有最强应着，直接给 ±MATE / 0。
 *   ANALYZE_TIME 把单局面预算从 800ms 调到 420ms：复盘是 N+1 次搜索的乘法，
 *   省的是分钟级；迭代加深保证「算不完就退回上一完整层」，深度 3 绝大多数局面在
 *   预算内算完，表现为「快一倍」，棋力结论不变。
 *   同一次搜索顺带存下 bests[i]（引擎建议着）与 tops[i]（top-3 候选与分差）——
 *   「亏了多少」之外还要说「该走哪」，这是报告的核心增量。
 *
 * 纪律：全程零网络请求；分析按「一个局面一拍」切片跑，页面不冻结、进度条如实推进；
 *   动态内容一律 createElement + textContent；报告只存 SAN 与分差（讲解恢复时重算）。
 *   「看变化」（PV 主变）按需才算：点开一条结论时算一次（几百毫秒），不进主循环。
 */
(function (global) {
  'use strict';

  var doc = global.document;
  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* ST_UI 由 study.js 提供，footer 里它排在本文件之前（defer 按文档顺序执行） */
  var UI = null;
  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }
  function span(cls, text) {
    var n = el('span', cls);
    n.textContent = text;
    return n;
  }
  function icon(name) {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'tool-icon');
    svg.setAttribute('aria-hidden', 'true');
    var use = doc.createElementNS(SVG_NS, 'use');
    use.setAttribute('href', '#i-' + name);
    svg.appendChild(use);
    return svg;
  }

  var RULES_SRC = '/static/study/vendor/chesslib.min.js';
  /* 存储键带棋种前缀：复盘的规则层与引擎是国际象棋专属，将来象棋 / 围棋复盘各自独立成页 */
  var STORE_KEY = 'study-guoji-xiangqi-fupan-report';
  var HIST_KEY = 'study-guoji-xiangqi-fupan-history';
  var HIST_MAX = 5;        // 最近棋谱留几盘：复盘完关页面不再「下一盘挤掉上一盘」
  var ANALYZE_TIME = 420;  // 复盘单局面预算（毫秒）：N+1 次搜索的乘法，快一倍、深度结论不变
  var START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  var GLYPH = { p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚' };
  var PIECE_CN = { p: '兵', n: '马', b: '象', r: '车', q: '后', k: '王' };
  var FILES = 'abcdefgh';
  var ANALYZE_LEVEL = 2;   // 与对战页「进阶」同档：3 层 + 静态化延伸，深度与耗时的折中
  var MATE = 100000;
  var REPORT_MIN = 80;     // 亏到多少（厘兵）才列进结论：浅引擎对平静着法有噪声，门槛放明面上
  var KIND_CN = { blunder: '漏着', mistake: '错着', inaccuracy: '不精确' };
  var HANDOFF_KEY = 'study-guoji-xiangqi-fupan-handoff';

  var root = doc.querySelector('[data-st-guoji-xiangqi-fupan]');
  var E = {};

  /* ---------------- 规则库动态注入（并发共享一次，失败如实说明） ---------------- */
  var rulesPending = null;
  function ensureRules(done) {
    if (global.ChessLib && global.ChessLib.Chess) { done(null); return; }
    if (rulesPending) { rulesPending.push(done); return; }
    rulesPending = [done];
    var s = doc.createElement('script');
    var ver = (root.getAttribute('data-st-ver') || '').replace(/[^0-9a-zA-Z.]/g, '');
    s.src = RULES_SRC + (ver ? '?v=' + ver : '');
    s.onload = function () { settle(global.ChessLib && global.ChessLib.Chess ? null : 'fail'); };
    s.onerror = function () { settle('fail'); };
    (doc.head || doc.documentElement).appendChild(s);
  }
  function settle(err) {
    var cbs = rulesPending || [];
    rulesPending = null;
    for (var i = 0; i < cbs.length; i++) { cbs[i](err); }
  }

  /* ---------------- 音效：与对战页同款 WebAudio 现场合成（零采样零请求） ----------------
   * 落子 / 吃子 / 将军 / 将杀四声，触发与飞子同条件（回放与逐格回看推进响，跳转不响）。
   * 开关与对战页共享同一本机偏好 study-chess-sound——对战页「落子音效」开关一处控制两页，
   * 本页不重复摆开关。无 AudioContext（jsdom 等）静默可玩。 */
  var SFX = {
    ctx: null, master: null, on: true,
    load: function () {
      try { this.on = global.localStorage.getItem('study-chess-sound') !== 'off'; } catch (e) { this.on = true; }
    },
    ok: function () {
      if (!this.on) { return false; }
      if (this.ctx) { return true; }
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) { return false; }
      try {
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.6;
        this.master.connect(this.ctx.destination);
      } catch (e) { this.ctx = null; return false; }
      return true;
    },
    ensure: function () {
      if (!this.ok()) { return false; }
      if (this.ctx.state === 'suspended') {
        var p = this.ctx.resume();
        if (p && p['catch']) { p['catch'](function () { /* 非手势环境：这次不响，下次手势里再试 */ }); }
      }
      return true;
    },
    warm: function () { this.ensure(); },
    /* 木质敲击：短噪声脉冲过带通（频谱冲击）+ 低频正弦（棋盘箱体共鸣）。at = 音频时间轴偏移 */
    knock: function (freq, vol, decay, at) {
      var ctx = this.ctx;
      var t = ctx.currentTime + (at || 0);
      var len = Math.max(1, Math.floor(ctx.sampleRate * 0.06));
      var buf = ctx.createBuffer(1, len, ctx.sampleRate);
      var d = buf.getChannelData(0);
      for (var i = 0; i < len; i++) { d[i] = (Math.random() * 2 - 1) * (1 - i / len); }
      var src = ctx.createBufferSource();
      src.buffer = buf;
      var bp = ctx.createBiquadFilter();
      bp.type = 'bandpass'; bp.frequency.value = freq; bp.Q.value = 1.1;
      var g = ctx.createGain();
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + decay);
      src.connect(bp); bp.connect(g); g.connect(this.master);
      src.start(t);
      var g2 = ctx.createGain();
      g2.gain.setValueAtTime(vol * 0.5, t);
      g2.gain.exponentialRampToValueAtTime(0.001, t + decay * 1.6);
      g2.connect(this.master);
      var o = ctx.createOscillator();
      o.type = 'sine'; o.frequency.value = 150;
      o.connect(g2); o.start(t); o.stop(t + decay * 1.8);
    },
    /* 短音（triangle）：将杀音型与将军警示共用 */
    tone: function (freq, dur, vol, at) {
      var ctx = this.ctx;
      var t = ctx.currentTime + (at || 0);
      var g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(vol, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      g.connect(this.master);
      var o = ctx.createOscillator();
      o.type = 'triangle'; o.frequency.value = freq;
      o.connect(g); o.start(t); o.stop(t + dur + 0.1);
    },
    move: function () {
      if (!this.ensure()) { return; }
      this.knock(1000, 0.5, 0.08);
    },
    capture: function () {
      if (!this.ensure()) { return; }
      this.knock(520, 0.65, 0.13);
      this.knock(1250, 0.3, 0.09, 0.05);
    },
    checkSfx: function () {
      if (!this.ensure()) { return; }
      this.tone(660, 0.07, 0.22, 0);
      this.tone(880, 0.09, 0.22, 0.09);
    },
    mate: function (won) {
      if (!this.ensure()) { return; }
      var seq = won
        ? [[261.63, 0], [329.63, 0.12], [392, 0.24], [523.25, 0.36]]   // C4 E4 G4 C5：上行大三和弦
        : [[329.63, 0], [261.63, 0.16], [220, 0.32]];                  // E4 C4 A3：下行小三音
      for (var i = 0; i < seq.length; i++) { this.tone(seq[i][0], 0.42, 0.34, seq[i][1]); }
    },
    /** 从 SAN 判这一步配什么声。复盘没有「你赢你输」的立场，将杀统一播上行音型。 */
    fromSan: function (san) {
      if (!this.on || !san) { return; }
      if (san.indexOf('#') >= 0) { this.mate(true); }
      else if (san.indexOf('+') >= 0) { this.checkSfx(); }
      else if (san.indexOf('x') >= 0) { this.capture(); }
      else { this.move(); }
    }
  };

  /* ---------------- 复盘状态 ---------------- */
  var sans = [];        // 每半回合的 SAN
  var plies = [];       // { san, mover, from, to, explain, loss, kind }
  var scores = [];      // scores[i] = 第 i 步之前的局面分（白方视角厘兵），长度 N+1
  var bests = [];       // bests[i] = 第 i 步之前局面里引擎的最佳着 SAN（「该走哪」）
  var tops = [];        // tops[i] = 同局面 top-3 候选 [{san, loss}]（loss = 与最佳的分差，厘）
  var pvs = {};         // pvs[i] = 第 i 步之前局面的主变 SAN 数组（点「看变化」才算，缓存）
  var analyzed = false;
  var analyzing = false;
  var viewAt = null;    // 回看中的行子下标（null = 终局）
  var replayTimer = null;
  var paintedPly = null;   // 上一次画到第几个半回合（null = 还没画过）——决定该不该飞子
  var savedNote = '';

  /* ---------------- 报告存本机：只存 SAN 与分差，讲解恢复时按引擎重算 ---------------- */
  function save() {
    try {
      global.localStorage.setItem(STORE_KEY, JSON.stringify({
        v: 1, sans: sans, losses: plies.map(function (p) { return p.loss; })
      }));
    } catch (e) { /* 隐私模式或配额满：复盘照做，只是不跨页保存 */ }
    saveHistory();
  }
  /* 最近棋谱：本机留最近 5 盘（SAN + 分差，讲解载入时重算）。仍是「存本地」——
     只进这台设备的 localStorage，不上传。存不进就算了，不弹错。 */
  function saveHistory() {
    try {
      var list = [];
      try { list = JSON.parse(global.localStorage.getItem(HIST_KEY) || '[]'); } catch (e2) { list = []; }
      /* 同一盘棋（着法相同）无论从哪个入口重新分析，都只留最新一条——
         只跟第一条比会漏掉「从最近复盘的棋局载入旧盘再分析」的重复（列表里出现两条同棋）。 */
      var kept = [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].sans.join() !== sans.join()) { kept.push(list[i]); }
      }
      kept.unshift({ t: Date.now(), sans: sans.slice(), losses: plies.map(function (p) { return p.loss; }) });
      if (kept.length > HIST_MAX) { kept.length = HIST_MAX; }
      global.localStorage.setItem(HIST_KEY, JSON.stringify(kept));
    } catch (e) { /* 同上 */ }
  }
  function loadHistory() {
    try {
      var list = JSON.parse(global.localStorage.getItem(HIST_KEY) || '[]');
      return Object.prototype.toString.call(list) === '[object Array]' ? list : [];
    } catch (e) { return []; }
  }
  function lookupMove(g, san) {
    var list = g.moves({ verbose: true });
    for (var i = 0; i < list.length; i++) {
      if (list[i].san === san) { return list[i]; }
    }
    return null;
  }
  function staticScore(g) {
    if (g.isCheckmate()) { return g.turn() === 'w' ? -MATE : MATE; }
    if (g.isDraw()) { return 0; }
    return global.STChessEngine.evaluate(g).total;
  }
  /** 由 SAN + 分差重建整局（restore 与「最近复盘的棋局」载入共用）：SAN 重放 + annotate 重算讲解
      （每步两次静态评估，比搜索便宜得多）。局面分用静态评估近似——只喂评估条和状态行，
      报告结论用的是存下来的分差。bests/tops 搜索数据不在存档里，恢复后由 hydrate 补算。 */
  function buildFromSanLoss(sansIn, lossesIn) {
    var g = new global.ChessLib.Chess(START_FEN);
    var built = [];
    for (var i = 0; i < sansIn.length; i++) {
      var verbose = lookupMove(g, sansIn[i]);
      if (!verbose) { return false; }                 // 与规则不符（存档被手改过）→ 作废
      built.push({
        san: verbose.san, mover: verbose.color, from: verbose.from, to: verbose.to,
        explain: global.STChessEngine.annotate(g, verbose),
        loss: lossesIn[i],
        kind: global.STChessEngine.classify(lossesIn[i])
      });
      g.move(sansIn[i]);
    }
    hydrateEpoch++;   // 换局作废还在飞的补算链（见 hydrateSuggestions 的代际守卫）
    sans = sansIn.slice();
    plies = built;
    bests = []; tops = []; pvs = {};
    for (var k = 0; k < sans.length; k++) { bests.push(null); tops.push(null); }
    scores = [staticScore(new global.ChessLib.Chess(START_FEN))];
    var again = new global.ChessLib.Chess(START_FEN);
    for (var j = 0; j < sans.length; j++) {
      again.move(sans[j]);
      scores.push(staticScore(again));
    }
    analyzed = true;
    return true;
  }
  /** 恢复 / 载入后，给结论清单里的局面补算「引擎建议 + 备选」（每拍一个局面，不卡页面）。
      分析路径里这些数据是搜索顺带产生的，只有恢复路径缺——补齐后报告条目自动长出建议。 */
  var hydrateEpoch = 0;   // 补算链代际：换局 / 重新分析时 +1，旧链的异步结果一律作废
  function hydrateSuggestions() {
    if (!analyzed || analyzing) { return; }
    var need = [];
    for (var i = 0; i < plies.length; i++) {
      if (plies[i].loss >= REPORT_MIN && !bests[i]) { need.push(i); }
    }
    if (!need.length) { return; }
    var k = 0;
    var myEpoch = hydrateEpoch;   // 换局后 positionAt 算的已是新局面，旧链不许再写 bests[idx]
    (function step() {
      if (k >= need.length || !analyzed || myEpoch !== hydrateEpoch) {
        renderAll();
        return;
      }
      var idx = need[k++];
      var g = positionAt(idx);
      if (g && !g.isCheckmate() && !g.isDraw()) {
        var r = global.STChessEngine.bestMove(g, ANALYZE_LEVEL, ANALYZE_TIME);
        if (r) {
          bests[idx] = (r.top && r.top.length) ? r.top[0].san : r.move.san;
          tops[idx] = r.top || null;
        }
      } else { bests[idx] = null; }
      renderAll();
      yieldTick(step);
    })();
  }
  function restore() {
    var raw = null;
    try { raw = global.localStorage.getItem(STORE_KEY); } catch (e) { return; }
    if (!raw) { return; }
    var data = null;
    try { data = JSON.parse(raw); } catch (e) { return; }
    if (!data || data.v !== 1 || !data.sans || !data.sans.length
      || !data.losses || data.losses.length !== data.sans.length) { return; }
    if (!buildFromSanLoss(data.sans, data.losses)) { return; }
    savedNote = '已从本机恢复上一次的复盘（共 ' + sans.length + ' 步）。粘入新棋谱可重新分析。';
    hydrateSuggestions();
  }

  /* ---------------- 分析：搜索一拍一个局面，讲解一轮扫完 ---------------- */
  /* 让渡一拍用 MessageChannel 而不是 setTimeout：后台标签页里 setTimeout 被浏览器节流到
     秒级以上，用户「切走等它算完」时分析会爬行；MessageChannel 的宏任务不节流。 */
  var yieldTick = (function () {
    if (typeof global.MessageChannel === 'function') {
      var ch = new global.MessageChannel();
      var pending = null;
      ch.port1.onmessage = function () {
        var f = pending;
        pending = null;
        if (f) { f(); }
      };
      return function (fn) { pending = fn; ch.port2.postMessage(0); };
    }
    return function (fn) { global.setTimeout(fn, 0); };
  })();
  function terminalScore(g) {
    if (g.isCheckmate()) { return g.turn() === 'w' ? -MATE : MATE; }
    if (g.isDraw()) { return 0; }
    return null;
  }
  function analyze(pgnText, onFail) {
    var parsed = new global.ChessLib.Chess();
    try {
      parsed.loadPgn(pgnText);   // chess.js 1.4 缺省宽松解析，读得动各家导出的 PGN
    } catch (e) {
      onFail('这份文本不是能读的 PGN 棋谱（' + String(e && e.message ? e.message : e) + '）。'
        + '请确认是从对战页「导出棋谱」或正规棋类软件导出的完整棋谱。');
      return;
    }
    var list = parsed.history();
    if (!list.length) {
      onFail('这份 PGN 里没有着法（可能只有头部信息），没有可复盘的内容。');
      return;
    }

    stopReplay();
    hydrateEpoch++;   // 作废可能在飞的补算链
    sans = list;
    plies = [];
    scores = [];
    bests = []; tops = []; pvs = {};
    analyzed = false;
    analyzing = true;
    viewAt = null;
    savedNote = '';

    var N = sans.length;
    var i = 0;
    var replay = new global.ChessLib.Chess(START_FEN);

    function finish() {
      analyzing = false;
      analyzed = true;
      viewAt = null;
      save();
      setStatus('分析完成：结论在右侧「复盘结论」，点棋盘旁的行子或结论条目回看。');
      E.savedNote.hidden = true;   // 重新分析后「已从本机恢复」的旧提示作废
      renderAll();
    }
    /* 第一拍循环：每个局面搜索一次（scores[i] + 建议着 + 候选榜），随后把棋子推到下一局面。
       建议与候选来自同一次搜索——「亏了多少」和「该走哪」是同一份数据的两个输出。 */
    function searchChunk() {
      var term = terminalScore(replay);
      if (term !== null) {
        scores[i] = term;
        bests[i] = null;   // 终局没有建议着（已是将杀 / 逼和 / 和棋）
        tops[i] = null;
      } else {
        var r = global.STChessEngine.bestMove(replay, ANALYZE_LEVEL, ANALYZE_TIME);
        scores[i] = r.score;
        /* 建议着取候选榜首位（放水前的真实最佳）而不是 r.move——复盘档 noise=0 时两者相等，
           这样写不依赖「档位没开放水」这个隐式前提 */
        bests[i] = (r.top && r.top.length) ? r.top[0].san : (r.move ? r.move.san : null);
        tops[i] = r.top || null;
      }
      i++;
      renderProgress(i, N + 1);
      if (i <= N) { replay.move(sans[i - 1]); yieldTick(searchChunk); }
      else { annotateChunk(); }
    }
    /* 第二轮：讲解与分差（annotate 内部 make/undo，纯静态评估，一拍扫完不卡） */
    function annotateChunk() {
      var g = new global.ChessLib.Chess(START_FEN);
      for (var k = 0; k < N; k++) {
        var verbose = lookupMove(g, sans[k]);
        if (!verbose) {
          analyzing = false;
          onFail('棋谱第 ' + (k + 1) + ' 步（' + sans[k] + '）与规则不符，分析中止。');
          return;
        }
        var dir = verbose.color === 'w' ? 1 : -1;
        var loss = Math.max(0, Math.round((scores[k] - scores[k + 1]) * dir));
        plies.push({
          san: verbose.san, mover: verbose.color, from: verbose.from, to: verbose.to,
          explain: global.STChessEngine.annotate(g, verbose),
          loss: loss,
          kind: global.STChessEngine.classify(loss)
        });
        g.move(sans[k]);
      }
      finish();
    }
    renderAll();
    yieldTick(searchChunk);
  }
  function renderProgress(done, total) {
    E.progress.hidden = false;
    E.progressFill.style.width = Math.round(done / total * 100) + '%';
    /* 首拍附带如实预告的耗时：单局面预算 × 局面数，向上取整成秒 */
    var est = done === 1 ? '，预计约 ' + Math.max(1, Math.round(total * ANALYZE_TIME / 1000)) + ' 秒' : '';
    E.progressText.textContent = '正在分析第 ' + done + ' / ' + total + ' 个局面'
      + est + '（全部在本地算，没有请求服务器）';
    if (done >= total) { E.progress.hidden = true; }
  }

  /* ---------------- 渲染：棋盘（只读） ---------------- */
  /** 重放到「第 j 步之前」的局面（j = 0..N；建议着 / PV 都以这个局面为根） */
  function positionAt(j) {
    var g = new global.ChessLib.Chess(START_FEN);
    for (var i = 0; i < j; i++) {
      try { g.move(sans[i]); } catch (e) { return null; }
    }
    return g;
  }
  function positionNow() {
    // sans 全部经过规则校验，正常不会走到兜底；兜底只为「存档被手改过」这类极端情况保命
    return positionAt(viewAt === null ? sans.length : viewAt + 1) || new global.ChessLib.Chess(START_FEN);
  }
  function checkedKing(board) {
    var g = positionNow();
    if (!g.inCheck()) { return null; }
    var turn = g.turn();
    for (var r = 0; r < 8; r++) {
      for (var c = 0; c < 8; c++) {
        var pc = board[r][c];
        if (pc && pc.type === 'k' && pc.color === turn) {
          return FILES.charAt(c) + String(8 - r);
        }
      }
    }
    return null;
  }
  function renderBoard() {
    var g = positionNow();
    var board = g.board();
    var checkSq = checkedKing(board);
    var box = E.board;
    var shown = viewAt === null ? plies.length - 1 : viewAt;
    var cue = shown >= 0 ? plies[shown] : null;
    box.textContent = '';
    for (var r = 0; r < 8; r++) {
      for (var c = 0; c < 8; c++) {
        var sq = FILES.charAt(c) + String(8 - r);
        var btn = el('button', 'st-sq' + ((r + c) % 2 === 1 ? ' st-sq--dark' : ''));
        btn.type = 'button';
        btn.setAttribute('data-sq', sq);
        var pc = board[r][c];
        btn.setAttribute('aria-label', sq + (pc
          ? ' ' + (pc.color === 'w' ? '白' : '黑') + PIECE_CN[pc.type] : ' 空'));
        if (c === 0) { btn.appendChild(span('st-sq-coord st-sq-coord--rank', String(8 - r))); }
        if (r === 7) { btn.appendChild(span('st-sq-coord st-sq-coord--file', FILES.charAt(c))); }
        if (pc) {
          var piece = el('span', 'st-piece st-piece--' + pc.color);
          piece.textContent = GLYPH[pc.type];
          btn.appendChild(piece);
        }
        if (cue && (sq === cue.from || sq === cue.to)) { btn.className += ' is-last'; }
        if (checkSq === sq) { btn.className += ' is-check'; }
        box.appendChild(btn);
      }
    }
    // 与对战页同一口径：只有逐格往前推进一步才飞子，点结论条目跳转不飞
    if (UI.fly && cue && paintedPly !== null && shown === paintedPly + 1) {
      UI.fly(box, cue.from, cue.to);
      /* 与飞子同条件发声：回放与逐格回看推进响，跳转/后退不响 */
      SFX.fromSan(cue.san);
    }
    paintedPly = shown;
  }

  /* ---------------- 渲染：行子列表与回看 ---------------- */
  function renderMoves() {
    var box = E.moves;
    box.textContent = '';
    if (!plies.length) {
      box.removeAttribute('aria-hidden');
      return;
    }
    var head = el('li', 'st-move-row st-move-row--head');
    head.appendChild(span('st-move-no', '#'));
    head.appendChild(span('st-move-col', '白方'));
    head.appendChild(span('st-move-col', '黑方'));
    box.appendChild(head);
    for (var i = 0; i < plies.length; i += 2) {
      var row = el('li', 'st-move-row');
      row.appendChild(span('st-move-no', (i / 2 + 1) + '.'));
      for (var k = 0; k < 2; k++) {
        var idx = i + k;
        if (idx >= plies.length) { row.appendChild(el('span', 'st-move-ply')); continue; }
        var p = plies[idx];
        var b = el('button', 'st-move-ply');
        b.type = 'button';
        b.textContent = p.san;
        b.setAttribute('data-ply', String(idx));
        if (p.loss >= REPORT_MIN) { b.className += ' is-lose'; }
        if (viewAt === idx) { b.className += ' is-cur'; }
        b.title = (p.mover === 'w' ? '白方 · ' : '黑方 · ') + phrasesText(p);
        b.addEventListener('click', onPly);
        row.appendChild(b);
      }
      box.appendChild(row);
    }
  }
  function phrasesText(p) {
    var ph = p.explain && p.explain.phrases ? p.explain.phrases : [];
    var head = lossText(p);
    return head + '：' + (ph.length ? ph.join('；') : '平静的调动，五项评估基本没变');
  }
  /* 厘兵 → 个兵，保留 1 位；≥1000 加千位分隔（「1000.0」不好读，「1,000.0」一眼有数） */
  function pawnsText(centis) {
    var s = (Math.round(centis / 10) / 10).toFixed(1);
    var parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }
  function lossText(p) {
    // 大额分差只可能是「走子方自己走进被将杀」（将杀对方的那步分差为 0 或为正，不会进这里）
    if (p.loss >= MATE / 2) { return '这步之后被将杀'; }
    if (p.loss <= 0) { return '没亏'; }
    return '亏 ' + pawnsText(p.loss) + ' 个兵';
  }
  function onPly(e) {
    if (replayTimer) { stopReplay(); }
    var idx = Number(e.currentTarget.getAttribute('data-ply'));
    viewAt = (idx === plies.length - 1 || viewAt === idx) ? null : idx;
    renderAll();
  }
  function stepPly(dir) {
    if (analyzing || !analyzed) { return; }
    stopReplay();
    var d = viewAt === null ? plies.length - 1 : viewAt;
    var n = d + dir;
    if (n < 0) { return; }
    viewAt = (n >= plies.length - 1) ? null : n;
    renderAll();
  }

  /* 「回放这局」：0.9 秒/半回合从开局重演，与对战页同一手感；再点 = 停止并回到终局 */
  function stopReplay() {
    if (replayTimer) { global.clearInterval(replayTimer); replayTimer = null; }
    if (E.replayLabel) { E.replayLabel.textContent = '回放这局'; }
  }
  function toggleReplay() {
    if (analyzing || !analyzed) { return; }
    if (replayTimer) { stopReplay(); renderAll(); return; }
    viewAt = 0;
    renderAll();
    replayTimer = global.setInterval(function () {
      if (viewAt === null || viewAt >= plies.length - 1) { stopReplay(); viewAt = null; renderAll(); return; }
      viewAt++;
      renderAll();
    }, 900);
    if (E.replayLabel) { E.replayLabel.textContent = '停止回放'; }
  }

  /* ---------------- 渲染：评估条（近似胜率）/ 状态 / 按钮 ---------------- */
  /* 数字换成胜率：对初学者「白 63%」比「+0.8」好读得多。换算是同一条 logistic 曲线
     （与条宽同一口径），浅引擎的分本来就有噪声，所以悬停提示里写明「近似」并附厘兵原值。 */
  function renderEval() {
    var idx = viewAt === null ? scores.length - 1 : viewAt + 1;
    var s = scores.length ? scores[idx] : 0;
    if (typeof s !== 'number') { s = 0; }
    var pct = 1 / (1 + Math.exp(-Math.max(-2000, Math.min(2000, s)) / 220));
    E.evalFill.style.width = (pct * 100).toFixed(1) + '%';
    if (Math.abs(s) >= MATE / 2) {
      E.evalNum.textContent = s > 0 ? '白胜 100%（将杀）' : '黑胜 100%（将杀）';
      E.evalNum.title = '将杀 / 逼和：结果已定';
      return;
    }
    var whitePct = Math.round(pct * 100);
    E.evalNum.textContent = (whitePct >= 50 ? '白 ' : '黑 ') + Math.max(whitePct, 100 - whitePct) + '%';
    var pawns = s / 100;
    E.evalNum.title = '近似胜率（本地引擎分换算，约 ' + (pawns >= 0 ? '+' : '−')
      + Math.abs(pawns).toFixed(1) + ' 兵）——浅引擎的换算仅供参考';
  }
  function renderStatus() {
    var box = E.viewStatus;
    box.className = 'st-status';
    if (analyzing) {
      box.textContent = '分析中：每个局面都要算一次最强应着，走完为止。';
      return;
    }
    if (!analyzed) {
      box.textContent = '还没有分析结果：粘入棋谱点「开始分析」，或从对战页点「复盘这一局」过来。';
      return;
    }
    if (replayTimer) {
      box.textContent = '回放中：重演到第 ' + (Math.floor(viewAt / 2) + 1) + ' 回合。';
      return;
    }
    if (viewAt !== null) {
      var p = plies[viewAt];
      box.textContent = '回看第 ' + (Math.floor(viewAt / 2) + 1) + ' 回合（'
        + (p.mover === 'w' ? '白方' : '黑方') + ' ' + p.san + '）之后的局面。';
      return;
    }
    var last = plies[plies.length - 1];
    box.textContent = sans.length >= 2 && last && last.explain && last.explain.mate
      ? '终局：将杀，共 ' + Math.ceil(sans.length / 2) + ' 回合。'
      : '终局：共 ' + Math.ceil(sans.length / 2) + ' 回合。';
  }
  function renderButtons() {
    var ready = analyzed && !analyzing;
    var d = viewAt === null ? plies.length - 1 : viewAt;
    E.prev.disabled = !ready || d <= 0;
    E.next.disabled = !ready || viewAt === null;
    E.replay.disabled = !ready || plies.length === 0;
    E.analyze.disabled = analyzing;
    if (E.reportCopy) { E.reportCopy.disabled = !ready; }
    if (E.reportDownload) { E.reportDownload.disabled = !ready; }
  }

  /* ---------------- 渲染：复盘结论 ---------------- */
  function renderReport() {
    var listBox = E.reportList;
    listBox.textContent = '';
    if (!analyzed) {
      E.reportCounts.textContent = analyzing ? '分析中……' : '分析完成后出结论。';
      return;
    }
    var count = { w: { blunder: 0, mistake: 0 }, b: { blunder: 0, mistake: 0 } };
    var worst = null;
    for (var i = 0; i < plies.length; i++) {
      var p = plies[i];
      if (p.loss >= REPORT_MIN && count[p.mover][p.kind] !== undefined) { count[p.mover][p.kind]++; }
      if (!worst || p.loss > worst.loss) { worst = p; }
    }
    var turnLine = '白方：漏着 ' + count.w.blunder + ' · 错着 ' + count.w.mistake
      + '　黑方：漏着 ' + count.b.blunder + ' · 错着 ' + count.b.mistake;
    if (worst && worst.loss >= REPORT_MIN) {
      turnLine += '　转折在第 ' + (Math.floor(plies.indexOf(worst) / 2) + 1) + ' 回合（'
        + (worst.mover === 'w' ? '白方' : '黑方') + ' ' + worst.san + '）';
    } else {
      turnLine += '　双方都没走出明显亏分的着法';
    }
    E.reportCounts.textContent = turnLine;

    var any = false;
    for (var j = 0; j < plies.length; j++) {
      var p2 = plies[j];
      if (p2.loss < REPORT_MIN) { continue; }
      any = true;
      var li = el('li', 'st-report-item');
      var btn = el('button', 'st-report-btn');
      btn.type = 'button';
      btn.appendChild(span('st-report-no', (Math.floor(j / 2) + 1) + '.'));
      btn.appendChild(span('st-report-san', p2.san));
      btn.appendChild(span('st-report-loss' + (p2.loss >= 180 ? ' st-report-loss--big' : ''), lossText(p2)));
      btn.appendChild(span('st-report-why', firstPhrase(p2)));
      btn.addEventListener('click', (function (idx) {
        return function () {
          stopReplay();
          viewAt = (idx === plies.length - 1) ? null : idx;
          renderAll();
        };
      })(j));
      li.appendChild(btn);
      li.appendChild(buildSuggestion(j, p2));   // 「该走哪」：建议着 + 备选 + 变化线
      listBox.appendChild(li);
    }
    if (!any) {
      var empty = el('li', 'st-report-empty');
      empty.textContent = '没有亏到 ' + (REPORT_MIN / 100).toFixed(1) + ' 个兵以上的着法——至少在这个引擎眼里下得干净。';
      listBox.appendChild(empty);
    }
  }
  function firstPhrase(p) {
    var ph = p.explain && p.explain.phrases ? p.explain.phrases : [];
    if (!ph.length) {
      // 静态五项看不出、但搜索分差大 = 亏在后续手段（对方有强手），如实说而不是硬编理由
      if (p.loss >= REPORT_MIN) { return '静态五项看不出亏在哪——这一步亏在对方的后续手段'; }
      return '五项评估基本没变';
    }
    return ph[0];
  }

  /* ---------------- 「该走哪」：建议着 + 备选 + 变化线 ----------------
     数据来自 analyze 时同一次搜索（bests/tops）与按需的 pvLine——
     「亏了多少」和「该怎么改」出自同一份计算，不另说一套。 */
  function buildSuggestion(j, p) {
    var box = el('div', 'st-report-best');
    var best = bests[j];
    if (best) {
      if (best === p.san) {
        // 你走的恰是引擎首选，分差来自相邻两次浅搜索的噪声——如实说，不让人白改
        box.appendChild(span('st-report-best-line', '引擎也选了这一手——上面的分差多半是浅算的噪声，不用改。'));
      } else {
        box.appendChild(span('st-report-best-line', '引擎建议：' + best));
        var alts = [];
        var topsList = tops[j] || [];
        for (var i = 0; i < topsList.length && alts.length < 2; i++) {
          var t = topsList[i];
          if (t.san !== best && t.san !== p.san) {
            alts.push(t.san + (t.loss > 0 ? '（差 ' + (t.loss / 100).toFixed(1) + '）' : '（并列）'));
          }
        }
        if (alts.length) { box.appendChild(span('st-report-alt', '备选：' + alts.join('、'))); }
      }
    } else if (analyzing) {
      box.appendChild(span('st-report-best-line', '引擎建议随分析补上……'));
    } else {
      // 恢复的旧报告：建议在后台逐个补算（hydrateSuggestions），到一条显一条
      box.appendChild(span('st-report-best-line', '引擎建议算好后出现在这里……'));
    }
    /* 变化线：点开才算一次（几百毫秒），算过就缓存；浅引擎的主变展示 2~3 步，如实不冒充证明 */
    var pvBtn = el('button', 'st-report-pv-btn');
    pvBtn.type = 'button';
    var pvLine = el('span', 'st-report-pv-line');
    if (pvs[j] && pvs[j].length) {
      pvLine.textContent = pvs[j].join(' ');
      pvLine.hidden = false;
      pvBtn.textContent = '收起变化';
    } else {
      pvLine.hidden = true;
      pvBtn.textContent = pvs[j] ? '变化算不出（超时）' : '看变化';
    }
    pvBtn.addEventListener('click', function () {
      if (pvs[j]) {
        pvLine.hidden = !pvLine.hidden;
        pvBtn.textContent = pvLine.hidden ? '看变化' : '收起变化';
        return;
      }
      pvBtn.disabled = true;
      pvBtn.textContent = '算变化中……';
      global.setTimeout(function () {
        var g = positionAt(j);
        var line = g ? global.STChessEngine.pvLine(g, ANALYZE_LEVEL) : [];
        pvs[j] = line;
        pvBtn.disabled = false;
        if (line.length) {
          pvLine.textContent = line.join(' ');
          pvLine.hidden = false;
          pvBtn.textContent = '收起变化';
        } else {
          pvBtn.textContent = '变化算不出（超时）';
        }
      }, 30);
    });
    box.appendChild(pvBtn);
    box.appendChild(pvLine);
    return box;
  }

  /* ---------------- 教练的话（本地聚合，句句有据） ----------------
     不接任何大模型：每句话都由本盘数据（分差、分档、阶段、评估项、建议着）模板生成，
     不会编账；语气刻意带人味、以鼓励收尾——复盘不该只有红叉，还得有「下一步改什么」。 */
  var PHASE_CN = ['前段', '中段', '后段'];
  var REASON_CN = {
    material: '丢子换子吃亏', threats: '漏看对方的威胁', kingSafety: '王城变薄',
    center: '中心失控', development: '出子偏慢'
  };
  function coachStats() {
    var N = plies.length;
    var b1 = Math.max(1, Math.round(N / 3)), b2 = Math.max(b1 + 1, Math.round(N * 2 / 3));
    function mk() {
      return { blunder: 0, mistake: 0, loss: 0, phCnt: [0, 0, 0], phLoss: [0, 0, 0], reasons: {}, worst: null, worstIdx: -1 };
    }
    var st = { w: mk(), b: mk() };
    for (var i = 0; i < plies.length; i++) {
      var p = plies[i];
      if (p.loss < REPORT_MIN) { continue; }
      var s = st[p.mover];
      if (!s) { continue; }
      if (p.kind === 'blunder') { s.blunder++; } else if (p.kind === 'mistake') { s.mistake++; }
      s.loss += p.loss;
      var ph = i < b1 ? 0 : (i < b2 ? 1 : 2);
      s.phCnt[ph]++; s.phLoss[ph] += p.loss;
      var d = p.explain && p.explain.deltas;
      if (d) {
        var keys = ['material', 'threats', 'kingSafety', 'center', 'development'];
        var bad = null, bv = 0;
        for (var k = 0; k < keys.length; k++) {
          var v = d[keys[k]];
          if (typeof v === 'number' && v < 0 && -v > bv) { bad = keys[k]; bv = -v; }
        }
        if (bad) { s.reasons[REASON_CN[bad]] = (s.reasons[REASON_CN[bad]] || 0) + 1; }
      }
      if (!s.worst || p.loss > s.worst.loss) { s.worst = p; s.worstIdx = i; }
    }
    return st;
  }
  function coachRound(k) { return Math.floor(k / 2) + 1; }
  function buildCoach() {
    var st = coachStats();
    var entries = st.w.blunder + st.w.mistake + st.b.blunder + st.b.mistake;
    var lines = [];
    if (!entries) {
      lines.push('这盘棋在这个引擎眼里下得很干净：双方都没有亏到 0.8 个兵以上的着法。'
        + '能把平静的局面走得稳，本身就是功夫——想再进一步，拿一盘输给更强对手的棋来复盘，收获更大。');
      return lines;
    }
    /* 总评：谁的亏分重、集中在哪一段（ thirds 分段如实叫前/中/后段，不冒充开局残局理论）。
       双方总亏分完全相等时不能说「比对方重」——如实说打平。 */
    var tie = st.w.loss === st.b.loss;
    var more = st.w.loss >= st.b.loss ? 'w' : 'b';
    var sM = st[more], sO = st[more === 'w' ? 'b' : 'w'];
    var nameM = more === 'w' ? '白方' : '黑方';
    var nameO = more === 'w' ? '黑方' : '白方';
    var phase = 0;
    for (var i = 1; i < 3; i++) { if (sM.phLoss[i] > sM.phLoss[phase]) { phase = i; } }
    var openers = ['先说最要紧的：', '整盘扫完，结论是：', '这盘棋的账是这样的：'];
    lines.push(openers[plies.length % openers.length] + (tie
      ? '双方亏分打成平手（各约 ' + pawnsText(sM.loss) + ' 个兵）'
      : nameM + '一共亏了约 ' + pawnsText(sM.loss) + ' 个兵，比' + nameO
        + '（约 ' + pawnsText(sO.loss) + ' 个兵）重')
      + '；亏分最集中在' + PHASE_CN[phase]
      + (sM.phCnt[phase] > 1 ? '，' + sM.phCnt[phase] + ' 处里占了大头' : '') + '。');
    /* 重复出现的问题优先讲——同一个坑摔两次就是习惯了 */
    var reason = null, rc = 0;
    for (var r in sM.reasons) { if (sM.reasons[r] > rc) { reason = r; rc = sM.reasons[r]; } }
    if (reason && rc >= 2) {
      lines.push('这些亏分里有一个反复出现的问题——' + reason + '（' + rc + ' 处）。'
        + '把它挑出来改，比多背十个开局都有用。');
    } else if (sM.worst) {
      var best = bests[sM.worstIdx];
      lines.push('最值得重摆的是第 ' + coachRound(sM.worstIdx) + ' 回合的 ' + sM.worst.san
        + (best && best !== sM.worst.san ? '——引擎建议走 ' + best : '')
        + '。把那一步前后多看两眼，胜过把整盘重下三遍。');
    }
    /* 亮点：只夸数据撑得住的 */
    var praise = [];
    if (sO.blunder === 0) { praise.push(nameO + '整盘没有漏着，稳定性值得保持'); }
    if (sO.loss > 0 && sO.phCnt[2] === 0) { praise.push(nameO + '后段一步都没亏，越下越稳'); }
    if (praise.length) { lines.push('好消息也有：' + praise.join('；') + '。'); }
    lines.push('复盘不是找罪受：从错着清单里挑一条，按「引擎建议」摆一遍——下一盘改掉一件事，就算这趟复盘没白做。');
    return lines;
  }
  function renderCoach() {
    if (!E.coach) { return; }
    if (!analyzed) { E.coach.hidden = true; E.coach.textContent = ''; return; }
    var lines = buildCoach();
    E.coach.textContent = '';
    E.coach.appendChild(el('h4', 'st-coach-title', '教练的话'));
    for (var i = 0; i < lines.length; i++) { E.coach.appendChild(el('p', 'st-coach-line', lines[i])); }
    E.coach.hidden = false;
  }

  /* ---------------- 报告导出（复制 / 下载 .txt）——分享出去也是免费替本页说话 ---------------- */
  function reportText() {
    var L = [];
    var count = { w: { blunder: 0, mistake: 0 }, b: { blunder: 0, mistake: 0 } };
    var worst = null, worstIdx = -1;
    for (var i = 0; i < plies.length; i++) {
      var p = plies[i];
      if (p.loss >= REPORT_MIN && count[p.mover][p.kind] !== undefined) { count[p.mover][p.kind]++; }
      if (!worst || p.loss > worst.loss) { worst = p; worstIdx = i; }
    }
    L.push('学习乐园 · 国际象棋复盘报告');
    L.push('生成时间：' + new Date().toLocaleString());
    L.push('规模：' + Math.ceil(sans.length / 2) + ' 回合（本地引擎分析，棋谱未上传）');
    L.push('白方：漏着 ' + count.w.blunder + ' · 错着 ' + count.w.mistake
      + '　黑方：漏着 ' + count.b.blunder + ' · 错着 ' + count.b.mistake);
    if (worst && worst.loss >= REPORT_MIN) {
      L.push('转折：第 ' + coachRound(worstIdx) + ' 回合（' + (worst.mover === 'w' ? '白方' : '黑方') + ' ' + worst.san + '）');
    }
    L.push('');
    L.push('—— 错着清单 ——');
    for (var j = 0; j < plies.length; j++) {
      var p2 = plies[j];
      if (p2.loss < REPORT_MIN) { continue; }
      L.push('第 ' + coachRound(j) + ' 回合 ' + (p2.mover === 'w' ? '白' : '黑') + ' ' + p2.san
        + '：' + lossText(p2) + '（' + KIND_CN[p2.kind] + '）');
      var ph = p2.explain && p2.explain.phrases ? p2.explain.phrases : [];
      if (ph.length) { L.push('  原因：' + ph.join('；')); }
      var best = bests[j];
      if (best && best !== p2.san) {
        L.push('  引擎建议：' + best);
        var topsList = tops[j] || [];
        var alts = [];
        for (var k = 0; k < topsList.length && alts.length < 2; k++) {
          var t = topsList[k];
          if (t.san !== best && t.san !== p2.san) { alts.push(t.san + (t.loss > 0 ? '（差 ' + (t.loss / 100).toFixed(1) + '）' : '（并列）')); }
        }
        if (alts.length) { L.push('  备选：' + alts.join('、')); }
      }
      if (pvs[j] && pvs[j].length) { L.push('  变化：' + pvs[j].join(' ')); }
    }
    L.push('');
    L.push('—— 教练的话 ——');
    var coach = buildCoach();
    for (var c = 0; c < coach.length; c++) { L.push(coach[c]); }
    L.push('');
    L.push('口径：本地引擎每步约 3 层，亏分门槛 0.8 个兵；平静着法的小分差仅供参考。');
    return L.join('\n');
  }
  function fileStamp() {
    var d = new Date();
    function p2(n) { return (n < 10 ? '0' : '') + n; }
    return d.getFullYear() + p2(d.getMonth() + 1) + p2(d.getDate()) + '-' + p2(d.getHours()) + p2(d.getMinutes());
  }
  function copyReport() {
    if (!analyzed || analyzing) { return; }
    var text = reportText();
    var fail = function () {
      var ta = doc.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      doc.body.appendChild(ta);
      ta.select();
      try { ta.setSelectionRange(0, text.length); } catch (e2) { /* 老浏览器无所谓 */ }
      var okCopy = false;
      try { okCopy = doc.execCommand('copy'); } catch (e3) { okCopy = false; }
      /* execCommand 被拒时返回 false 而不抛异常，必须看返回值，否则明明没复制成功也说复制了 */
      setStatus(okCopy ? '报告已复制，去粘贴吧。' : '复制被浏览器拦了——试试「下载报告」。');
      doc.body.removeChild(ta);
    };
    if (global.navigator && global.navigator.clipboard && global.navigator.clipboard.writeText) {
      global.navigator.clipboard.writeText(text).then(function () { setStatus('报告已复制，去粘贴吧。'); }, fail);
    } else { fail(); }
  }
  function downloadReport() {
    if (!analyzed || analyzing) { return; }
    var blob = new Blob([reportText()], { type: 'text/plain;charset=utf-8' });
    var a = doc.createElement('a');
    a.href = global.URL.createObjectURL(blob);
    a.download = '学习乐园-国际象棋复盘-' + fileStamp() + '.txt';
    doc.body.appendChild(a);
    a.click();
    doc.body.removeChild(a);
    global.setTimeout(function () { global.URL.revokeObjectURL(a.href); }, 2000);
    setStatus('报告已下载成 .txt——存档、发群都行。');
  }

  /* ---------------- 最近复盘的棋局（本机最近 5 盘，载入即回看） ---------------- */
  function renderGames() {
    if (!E.games || !E.gamesList) { return; }
    var list = loadHistory();
    if (E.gamesCount) { E.gamesCount.textContent = list.length ? String(list.length) : ''; }
    disarmDel();
    E.gamesList.textContent = '';   // 先清旧条目：删掉最后一条后不留过期 DOM
    if (!list.length) { E.games.hidden = true; return; }
    E.games.hidden = false;
    for (var i = 0; i < list.length; i++) {
      (function (entry, idx) {
        var li = el('li', 'st-games-item');
        var d = new Date(entry.t || Date.now());
        function p2(n) { return (n < 10 ? '0' : '') + n; }
        // 日期主行说人话：今天/昨天用相对词，更早回退「9月28日」——比 10-1 这种数字串好读。
        // 按日历日判断而不是 24 小时差：凌晨的棋到上午打开也该算「昨天」
        var now = new Date();
        var day0 = function (x) { return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime(); };
        var diff = Math.round((day0(now) - day0(d)) / (24 * 3600 * 1000));
        var hm = p2(d.getHours()) + ':' + p2(d.getMinutes());
        var when;
        if (diff <= 0) { when = '今天 ' + hm; }
        else if (diff === 1) { when = '昨天 ' + hm; }
        else { when = (d.getMonth() + 1) + '月' + d.getDate() + '日 ' + hm; }
        // 主副两层：日期时间是扫读主键（深色），「走了 N 回合」是次要信息（浅色小字）
        var label = el('span', 'st-games-label');
        label.appendChild(span('st-games-date', when));
        label.appendChild(span('st-games-meta', '走了 ' + Math.ceil((entry.sans || []).length / 2) + ' 回合'));
        li.appendChild(label);
        // 整条可点：点条目任意处加载（鼠标点「加载」/ 键盘 Enter 都靠 click 冒泡到这里，不重复绑）；
        // 删除按钮有自己的语义，stopPropagation 拦住不让它顺带触发载入
        li.addEventListener('click', function (e) {
          if (e.target.closest && e.target.closest('.st-games-del')) { return; }
          loadHistoryGame(idx);
        });
        var btn = el('button', 'st-games-open');
        btn.type = 'button';
        btn.textContent = '加载';
        btn.setAttribute('aria-label', '加载 ' + when + ' 的那盘棋');
        li.appendChild(btn);
        var del = el('button', 'st-games-del');
        del.type = 'button';
        del.textContent = '删除';
        del.setAttribute('data-aria-rest', '删除 ' + when + ' 那盘的本机记录');
        del.setAttribute('aria-label', del.getAttribute('data-aria-rest'));
        del.addEventListener('click', function (e) {
          e.stopPropagation();
          if (armedDel === del) { disarmDel(); removeHistoryGame(idx); return; }
          disarmDel();
          armedDel = del;
          del.textContent = '确认删除？';
          del.classList.add('st-games-del--armed');
          del.setAttribute('aria-label', '再点一次确认：' + del.getAttribute('data-aria-rest'));
          armedDelTimer = setTimeout(disarmDel, 3000);
        });
        li.appendChild(del);
        E.gamesList.appendChild(li);
      })(list[i], i);
    }
  }
  function loadHistoryGame(idx) {
    if (analyzing) { return; }
    var entry = loadHistory()[idx];
    if (!entry || !entry.sans || !entry.losses) {
      setStatus('这一条记录缺了亏分数据（可能是很早版本的存档），载不了——把那盘棋谱重新粘进来分析一次就好。');
      return;
    }
    stopReplay();
    if (!buildFromSanLoss(entry.sans, entry.losses)) {
      setStatus('这一盘的存档与规则对不上（可能被改动过），载不了。');
      return;
    }
    viewAt = null;
    paintedPly = null;
    E.pgn.value = pgnFromSans();
    savedNote = '已加载最近的一盘（' + plies.length + ' 步）。粘入新棋谱可重新分析。';
    renderAll();
    hydrateSuggestions();
  }
  /** 删除做内联两步确认：第一击按钮就地变「确认删除？」，再击才真删，3 秒不动自动复位。
      误删的代价是整段本机分析记录，弹窗太重、一点就删太轻。armed 状态随重渲染/换目标清掉。 */
  var armedDel = null;
  var armedDelTimer = 0;
  function disarmDel() {
    if (armedDelTimer) { clearTimeout(armedDelTimer); armedDelTimer = 0; }
    if (armedDel) {
      armedDel.textContent = '删除';
      armedDel.classList.remove('st-games-del--armed');
      armedDel.setAttribute('aria-label', armedDel.getAttribute('data-aria-rest') || '');
      armedDel = null;
    }
  }
  /** 删除「最近复盘的棋局」里的一条——只删本机记录，不影响当前画布与已下载的报告；须经内联确认后调用 */
  function removeHistoryGame(idx) {
    if (analyzing) { return; }
    var list = loadHistory();
    if (idx < 0 || idx >= list.length) { return; }
    list.splice(idx, 1);
    try { global.localStorage.setItem(HIST_KEY, JSON.stringify(list)); } catch (e) { /* 存不进就算了 */ }
    renderGames();
  }
  function renderAll() {
    renderBoard();
    renderMoves();
    renderEval();
    renderStatus();
    renderButtons();
    renderReport();
    renderCoach();
    renderGames();
    if (savedNote) {
      E.savedNote.hidden = false;
      E.savedNote.textContent = savedNote;
      savedNote = '';
    }
    var cur = E.moves.querySelector('.is-cur');
    if (cur && cur.scrollIntoView) { cur.scrollIntoView({ block: 'nearest' }); }
  }

  /* ---------------- 导入 ---------------- */
  function doAnalyze() {
    if (analyzing) { return; }
    var text = E.pgn.value.replace(/\u00a0/g, ' ').trim();
    if (!text) {
      setStatus('先把棋谱文本粘进上面的框，或点「选 .pgn 文件」。');
      return;
    }
    setStatus('解析成功，开始逐步分析……');
    analyze(text, setStatus);
  }
  function setStatus(msg) {
    E.status.textContent = msg;
  }
  function readFile(f) {
    if (!f) { return; }
    if (!global.FileReader) { setStatus('这个浏览器不支持本地读文件，把棋谱文本直接粘进来也一样。'); return; }
    var fr = new global.FileReader();
    fr.onload = function () {
      E.pgn.value = String(fr.result || '');
      doAnalyze();
    };
    fr.onerror = function () { setStatus('文件读不出来（可能是空文件或编码不是文本）。'); };
    fr.readAsText(f);
  }

  /* ---------------- 启动 ---------------- */
  function boot() {
    UI = global.ST_UI;
    E = {
      pgn: pick('pgn'), file: pick('file'), analyze: pick('analyze'),
      status: pick('status'), progress: pick('progress'),
      progressFill: pick('progress-fill'), progressText: pick('progress-text'),
      board: pick('board'), viewStatus: pick('view-status'),
      evalFill: pick('eval-fill'), evalNum: pick('eval-num'),
      moves: pick('moves'), prev: pick('prev'), next: pick('next'),
      replay: pick('replay'), replayLabel: pick('replay-label'),
      reportCounts: pick('report-counts'), reportList: pick('report-list'),
      coach: pick('coach'), reportCopy: pick('report-copy'), reportDownload: pick('report-download'),
      games: pick('games'), gamesCount: pick('games-count'), gamesList: pick('games-list'),
      savedNote: pick('saved-note')
    };
    if (!E.board || !E.pgn || !global.STChessEngine) { return; }

    SFX.load();
    /* 任意点击（冒泡）先热一次音频：在用户手势栈内 resume AudioContext，
       之后「回放这局」900ms 一拍的非手势推进才有声；无音频环境是空操作 */
    root.addEventListener('click', function () { SFX.warm(); });

    E.analyze.addEventListener('click', doAnalyze);
    if (E.file) { E.file.addEventListener('change', function (e) { readFile(e.target.files[0]); }); }
    E.prev.addEventListener('click', function () { stepPly(-1); });
    E.next.addEventListener('click', function () { stepPly(1); });
    E.replay.addEventListener('click', toggleReplay);
    if (E.reportCopy) { E.reportCopy.addEventListener('click', copyReport); }
    if (E.reportDownload) { E.reportDownload.addEventListener('click', downloadReport); }

    /* 首次渲染必须等规则库就位：棋盘重放（positionNow）依赖 ChessLib，
       提前渲染会在 defer 脚本执行时报「ChessLib 未定义」。空档期棋盘区留白，
       由导入状态行说明当前在等什么。 */
    ensureRules(function (err) {
      if (err) {
        E.status.className = 'st-status is-warn';
        setStatus('棋类规则库没加载上（本机静态文件读取失败）。刷新一次通常就好；'
          + '反复出现说明这个站点的静态资源没部署全。');
        return;
      }
      // 从对战页「复盘这一局」带过来的棋谱：自动填入并直接开始分析
      var handoff = null;
      try { handoff = global.sessionStorage.getItem(HANDOFF_KEY); } catch (e) { handoff = null; }
      if (handoff && handoff.trim()) {
        try { global.sessionStorage.removeItem(HANDOFF_KEY); } catch (e2) { /* 读得出就清，清不掉也无害 */ }
        E.pgn.value = handoff;
        doAnalyze();
        return;
      }
      restore();
      if (analyzed) {
        E.pgn.value = pgnFromSans();
      }
      renderAll();
    });
  }
  /** 用当前 SAN 重建一份最小 PGN 填回输入框（恢复场景），头部只留结果与着法 */
  function pgnFromSans() {
    var out = [];
    for (var i = 0; i < sans.length; i += 2) {
      var line = (i / 2 + 1) + '. ' + sans[i];
      if (sans[i + 1]) { line += ' ' + sans[i + 1]; }
      out.push(line);
    }
    return out.join(' ');
  }
  function pick(name) { return root.querySelector('[data-st-' + name + ']'); }

  if (!root || !doc.querySelector('[data-st-board]')) { return; }
  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
